export const CONTRACT_ECONOMIC_ROLES = ["admin", "admin_manager", "project_manager"] as const;

export function canViewContractEconomics(role: string | null | undefined): boolean {
  return CONTRACT_ECONOMIC_ROLES.includes(role as any);
}

/**
 * Whether the user is allowed to edit contract economics (create/modify adjustments).
 * This is a role-based check.
 */
export function canEditContractEconomics(role: string | null | undefined): boolean {
  return CONTRACT_ECONOMIC_ROLES.includes(role as any);
}

export function canReviewContractAdjustment(role: string | null | undefined): boolean {
  return CONTRACT_ECONOMIC_ROLES.includes(role as any);
}

export function canDecideContractAdjustment(role: string | null | undefined): boolean {
  return role === "admin" || role === "admin_manager";
}

export function canCancelContractAdjustment(role: string | null | undefined, status: string): boolean {
  return canDecideContractAdjustment(role) && (status === "draft" || status === "pending_approval");
}

export function isClosedContractAdjustmentStatus(status: string): boolean {
  return status === "approved" || status === "rejected" || status === "cancelled";
}

/**
 * Whether an adjustment in the given status can be submitted for approval.
 */
export function canSubmitContractAdjustment(status: string): boolean {
  return status === "draft";
}

/**
 * Whether an adjustment in the given status can have its evidence managed.
 */
export function canManageContractEvidence(status: string): boolean {
  return status === "draft" || status === "pending_approval";
}

export const EVIDENCE_TYPES = [
  { value: "quotation", label: "Presupuesto" },
  { value: "purchase_order", label: "Pedido" },
  { value: "signed_annex", label: "Anexo firmado" },
  { value: "signed_contract", label: "Contrato firmado" },
  { value: "email_acceptance", label: "Aceptación por correo" },
  { value: "other", label: "Otro" },
] as const;

/**
 * Validate wizard step 1 form data (pure).
 * Returns { valid: boolean; errors: Record<string, string> }
 */
export function validateWizardStep1(data: { title?: string; reason?: string; amount?: string; effectiveDate?: string }): {
  valid: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};
  if (!data.title?.trim()) errors.title = "El título es obligatorio.";
  if (!data.reason?.trim()) errors.reason = "El motivo es obligatorio.";
  const amountError = validateMonetaryAmount(data.amount || "");
  if (amountError) errors.amount = amountError;
  if (!data.effectiveDate?.trim()) {
    errors.effectiveDate = "La fecha efectiva es obligatoria.";
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Check whether a set of evidence types qualifies for submission.
 * Requires at least one non-quotation evidence.
 */
export function hasQualifyingEvidence(evidenceTypes: string[]): boolean {
  return evidenceTypes.length > 0 && evidenceTypes.some(t => isQualifyingEvidenceType(t));
}

/** Qualifying evidence types that the backend accepts for approval. */
export const QUALIFYING_EVIDENCE_TYPES = new Set([
  "signed_annex",
  "signed_contract",
  "purchase_order",
  "email_acceptance",
]);

/** Whether an evidence type qualifies for approval. */
export function isQualifyingEvidenceType(type: string): boolean {
  return QUALIFYING_EVIDENCE_TYPES.has(type);
}

/**
 * Get the direction label in Spanish.
 */
export function getDirectionLabel(direction: string): string {
  return direction === "increase" ? "Incrementar" : "Reducir";
}

/**
 * Get a human-readable label for evidence count.
 */
export function getEvidenceLabel(count: number): string {
  return `${count} documento${count === 1 ? "" : "s"}`;
}

// ── Direction rules by adjustment type ────────────────────────────────

export type Direction = "increase" | "decrease";

/**
 * Returns the allowed directions for a given adjustment type.
 * contract_extension and additional_work: increase only.
 * contract_reduction: decrease only.
 * price_revision and other: both.
 */
export function getAllowedDirections(adjustmentType: string): Direction[] {
  switch (adjustmentType) {
    case "contract_extension":
    case "additional_work":
      return ["increase"];
    case "contract_reduction":
      return ["decrease"];
    case "price_revision":
    case "other":
      return ["increase", "decrease"];
    default:
      return ["increase", "decrease"];
  }
}

/** Whether a direction is valid for the given adjustment type. */
export function isDirectionAllowed(adjustmentType: string, direction: string): boolean {
  return getAllowedDirections(adjustmentType).includes(direction as Direction);
}

/**
 * Returns the default direction for a given adjustment type.
 * Types with only one allowed direction return that direction.
 */
export function getDefaultDirection(adjustmentType: string): Direction {
  return getAllowedDirections(adjustmentType)[0];
}

// ── Monetary validation ───────────────────────────────────────────────

const AMOUNT_REGEX = /^\d{1,13}(\.\d{1,2})?$/;

/**
 * Validates a monetary amount string.
 * Rejects: more than 2 decimals, non-numeric, zero, negative.
 * Accepts: 100, 100,5, 100,50, 1.250,50 (thousands separators stripped).
 * Returns null if valid, or an error message string.
 */
export function validateMonetaryAmount(value: string): string | null {
  if (!value.trim()) return "El importe es obligatorio.";
  // Normalize: remove thousands separators, normalize decimal comma
  const normalized = value
    .trim()
    .replace(/\.(?=\d{3}(?:\D|$))/g, "") // remove dots used as thousands sep
    .replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    return "El importe debe ser un número válido (ej: 15000, 1.250,50).";
  }
  const numericValue = parseFloat(normalized);
  if (numericValue <= 0) return "El importe debe ser mayor que 0.";
  // Check decimal places (up to 2)
  const parts = normalized.split(".");
  if (parts.length === 2 && parts[1].length > 2) {
    return "El importe no puede tener más de dos decimales.";
  }
  return null;
}

// ── Error mapping ─────────────────────────────────────────────────────

const KNOWN_ERROR_CODES: Record<string, string> = {
  FORBIDDEN: "No tienes permisos para realizar esta acción.",
  UNAUTHORIZED: "No tienes permisos para realizar esta acción.",
  NOT_FOUND: "El documento o la modificación ya no está disponible.",
  CONFLICT: "Este documento ya está vinculado a la modificación.",
};

/**
 * Map a contract adjustment error to a user-facing Spanish message.
 * @param error The error object from a failed mutation.
 * @param action A label for the action that failed (e.g. "vincular documento").
 */
export function mapContractAdjustmentError(error: unknown, action?: string): string {
  const err = error as any;
  const code = err?.code || err?.data?.code || "";
  const message = err?.message || "";

  if (KNOWN_ERROR_CODES[code]) return KNOWN_ERROR_CODES[code];

  if (
    message.toLowerCase().includes("status") ||
    message.toLowerCase().includes("closed") ||
    message.toLowerCase().includes("no se puede")
  ) {
    return "No pueden modificarse las evidencias de una modificación cerrada.";
  }

  if (action) {
    return `No se pudo ${action}. Inténtalo de nuevo.`;
  }
  return message || "No se pudo completar la operación. Inténtalo de nuevo.";
}

// ── Upload validation ─────────────────────────────────────────────────

export const ALLOWED_UPLOAD_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const ALLOWED_UPLOAD_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".webp"];

export const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Validates a file before upload.
 * Returns null if valid, or an error message string.
 */
export function validateUploadFile(file: File): string | null {
  if (!file) return "No se ha seleccionado ningún archivo.";

  // File size check
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    const mb = file.size / (1024 * 1024);
    return `El archivo es demasiado grande (${mb.toFixed(1)} MB). El tamaño máximo es 10 MB.`;
  }

  // MIME type check
  if (file.type && !ALLOWED_UPLOAD_MIME_TYPES.includes(file.type)) {
    return "Solo se permiten archivos PDF e imágenes (JPG, PNG, GIF, WebP).";
  }

  // Extension fallback check
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  if (extension && !ALLOWED_UPLOAD_EXTENSIONS.includes(extension)) {
    return "Solo se permiten archivos PDF e imágenes (JPG, PNG, GIF, WebP).";
  }

  return null;
}

export type ContractAdjustmentDirection = "increase" | "decrease";
export type ContractAdjustmentStatus =
  | "draft"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "cancelled";

export type ContractAdjustmentView = {
  id: number;
  adjustmentNumber: number;
  reference?: string | null;
  adjustmentType: string;
  direction: ContractAdjustmentDirection | string;
  amount: string | number;
  status: ContractAdjustmentStatus | string;
  title: string;
  reason?: string | null;
  effectiveDate?: string | Date | null;
  submittedAt?: string | Date | null;
  submittedByUserId?: number | null;
  approvedAt?: string | Date | null;
  approvedByUserId?: number | null;
  rejectedAt?: string | Date | null;
  rejectedByUserId?: number | null;
  rejectionReason?: string | null;
  cancelledAt?: string | Date | null;
  cancelledByUserId?: number | null;
  cancellationReason?: string | null;
  previousCeilingSnapshot?: string | number | null;
  resultingCeilingSnapshot?: string | number | null;
  createdByUserId?: number | null;
  createdAt?: string | Date | null;
  /** Historical row superseded by a legacy base-price update. */
  isAuditOnly?: boolean;
  supersededAt?: string | Date | null;
  supersededByLegacyCorrectionId?: number | null;
};

export type ContractCeilingView = {
  originalContractCents?: number;
  approvedIncreaseCents?: number;
  approvedDecreaseCents?: number;
  effectiveContractCeilingCents?: number;
  committedMilestonesCents?: number;
  availableContractBalanceCents?: number;
  originalContractAmount?: string | number;
  approvedIncreases?: string | number;
  approvedDecreases?: string | number;
  effectiveContractCeiling?: string | number;
  activeMilestonesTotal?: string | number;
  availableAmount?: string | number;
} | null;

export function parseMoneyToCents(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  if (!/^-?\d+(\.\d{1,})?$/.test(normalized)) return null;
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [euros, decimals = ""] = unsigned.split(".");
  const cents = Number(euros) * 100 + Number((decimals + "00").slice(0, 2));
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}

export function formatCents(cents: number, options?: { signed?: boolean }): string {
  const sign = options?.signed && cents > 0 ? "+" : cents < 0 ? "-" : "";
  const absolute = Math.abs(cents);
  const value = (absolute / 100).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });
  return `${sign}${value} €`;
}

export function formatMoneyValue(value: unknown, options?: { signed?: boolean }): string {
  return formatCents(parseMoneyToCents(value) ?? 0, options);
}

export function centsFromCeilingValue(centsValue: unknown, decimalValue?: unknown): number | null {
  if (typeof centsValue === "number" && Number.isFinite(centsValue)) return Math.round(centsValue);
  if (typeof centsValue === "string" && /^-?\d+$/.test(centsValue.trim())) return Number(centsValue);
  return parseMoneyToCents(decimalValue);
}

export function formatCalendarDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const raw = value instanceof Date ? value.toISOString() : value;
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("es-ES");
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("es-ES");
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
}

export function formatAdjustmentNumber(value: unknown): string {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "MC-—";
  return `MC-${String(number).padStart(3, "0")}`;
}

export function getAdjustmentTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    contract_extension: "Ampliación de contrato",
    additional_work: "Trabajo adicional",
    price_revision: "Revisión de precio",
    contract_reduction: "Reducción de alcance",
    other: "Otro",
  };
  return labels[type] || "Otro";
}

export function getEvidenceTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    signed_annex: "Anexo firmado",
    signed_contract: "Contrato firmado",
    purchase_order: "Pedido",
    email_acceptance: "Aceptación por correo",
    quotation: "Presupuesto",
    other: "Otro",
  };
  return labels[type] || "Otro";
}

export function getEvidenceQualificationLabel(type: string): string {
  return isQualifyingEvidenceType(type) ? "Cualificante" : "No cualificante";
}

export function getAdjustmentStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: "Borrador",
    pending_approval: "Pendiente de aprobación",
    approved: "Aprobada",
    rejected: "Rechazada",
    cancelled: "Cancelada",
  };
  return labels[status] || status;
}

export function isAuditOnlyContractAdjustment(
  adjustment: Pick<ContractAdjustmentView, "isAuditOnly" | "supersededAt">,
): boolean {
  // `supersededAt` is the persisted source of truth. The explicit boolean is
  // also accepted because the contract-first API exposes that projection.
  return adjustment.isAuditOnly === true || adjustment.supersededAt != null;
}

export function calculateApprovedDeltaCents(adjustments: ContractAdjustmentView[]) {
  return adjustments.reduce(
    (totals, adjustment) => {
      if (adjustment.status !== "approved") return totals;
      if (isAuditOnlyContractAdjustment(adjustment)) return totals;
      const amountCents = parseMoneyToCents(adjustment.amount) ?? 0;
      if (amountCents <= 0) return totals;
      if (adjustment.direction === "increase") totals.increaseCents += amountCents;
      if (adjustment.direction === "decrease") totals.decreaseCents += amountCents;
      return totals;
    },
    { increaseCents: 0, decreaseCents: 0 },
  );
}

export function summarizeContractAdjustments(adjustments: ContractAdjustmentView[]) {
  const statusCounts = adjustments.reduce<Record<string, number>>((counts, adjustment) => {
    counts[adjustment.status] = (counts[adjustment.status] ?? 0) + 1;
    return counts;
  }, {});
  const { increaseCents, decreaseCents } = calculateApprovedDeltaCents(adjustments);
  return {
    approvedIncreaseCents: increaseCents,
    approvedDecreaseCents: decreaseCents,
    approvedDeltaCents: increaseCents - decreaseCents,
    pendingCount: statusCounts.pending_approval ?? 0,
    statusCounts,
  };
}

export function getProjectedContractCeilingCents(currentCeilingCents: number, adjustment: ContractAdjustmentView): number {
  const amountCents = parseMoneyToCents(adjustment.amount) ?? 0;
  return adjustment.direction === "decrease"
    ? currentCeilingCents - amountCents
    : currentCeilingCents + amountCents;
}

export function getDecisionErrorMessage(error: unknown): string {
  const err = error as any;
  const code = err?.code || err?.data?.code || "";
  const message = err?.message || "";

  if (code === "FORBIDDEN" || code === "UNAUTHORIZED") return "No tienes permisos para realizar esta acción.";
  if (code === "CONFLICT") return "Esta modificación ya ha sido resuelta por otro usuario. Se actualizará la información.";
  if (message.includes("reducción dejaría el contrato")) return message;
  if (message.includes("Solo puede aprobarse") || message.includes("Solo puede rechazarse") || message.includes("Solo puede cancelarse")) {
    return "Esta modificación ya no admite esa acción. Se actualizará la información.";
  }
  return message || "No se pudo completar la decisión. Inténtalo de nuevo.";
}

export function summarizeContractCeiling(
  ceiling: ContractCeilingView,
  fallbackInitial: unknown,
  adjustments: ContractAdjustmentView[],
) {
  const adjustmentSummary = summarizeContractAdjustments(adjustments);
  const originalCents = centsFromCeilingValue(
    ceiling?.originalContractCents,
    ceiling?.originalContractAmount,
  ) ?? parseMoneyToCents(fallbackInitial) ?? 0;
  const approvedIncreaseCents = centsFromCeilingValue(
    ceiling?.approvedIncreaseCents,
    ceiling?.approvedIncreases,
  ) ?? adjustmentSummary.approvedIncreaseCents;
  const approvedDecreaseCents = centsFromCeilingValue(
    ceiling?.approvedDecreaseCents,
    ceiling?.approvedDecreases,
  ) ?? adjustmentSummary.approvedDecreaseCents;
  const effectiveContractCeilingCents = centsFromCeilingValue(
    ceiling?.effectiveContractCeilingCents,
    ceiling?.effectiveContractCeiling,
  ) ?? originalCents + approvedIncreaseCents - approvedDecreaseCents;
  const localEffectiveCents = originalCents + approvedIncreaseCents - approvedDecreaseCents;

  return {
    ...adjustmentSummary,
    originalCents,
    approvedIncreaseCents,
    approvedDecreaseCents,
    approvedDeltaCents: approvedIncreaseCents - approvedDecreaseCents,
    effectiveContractCeilingCents,
    localEffectiveCents,
    hasMismatch: Math.abs(effectiveContractCeilingCents - localEffectiveCents) > 1,
  };
}
